/**
 * usePublicPrices — fetches the live, override-aware price catalog so
 * marketing/display surfaces show the current admin-set amount instead
 * of a hardcoded JSX literal that goes stale every time pricing moves.
 *
 * Cached in module scope (one fetch per page-load shared across every
 * mounted consumer) so we don't fan a request out per <PriceTag>.
 * Refreshes on the next page navigation / hard reload.
 *
 * Usage:
 *   const { prices, fee } = usePublicPrices();
 *   const buyerPassMo = prices("buyer_pass_monthly", "$4.99");  // fmt string
 *   const buyerPassAmount = prices.amount("buyer_pass_monthly", 4.99);  // number
 *   const transportFee = fee("transport", 0.10);              // 0..1
 *   const transportFeePct = fee.pct("transport", 10);         // 0..100 (string-safe)
 *
 * Each accessor takes a fallback so the page never blanks while the
 * fetch is in flight — the fallback is the same literal that used to
 * be hardcoded.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

let _cache = null;        // { packages, platform_fees, fetched_at }
let _inflight = null;     // Promise<...>
const _subscribers = new Set();

async function _fetchOnce() {
  if (_cache && Date.now() - _cache.fetched_at < 60_000) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const r = await api.get("/public/prices");
      _cache = { ...r.data, fetched_at: Date.now() };
      _subscribers.forEach((fn) => fn(_cache));
      return _cache;
    } catch {
      // Network failure — every accessor will fall through to its
      // caller-provided fallback so the UI stays usable.
      return null;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

function _fmtUsd(amount) {
  if (amount == null) return null;
  // Whole-dollar amounts render without trailing ".00"; fractional
  // amounts keep two digits. With `style: "currency"` you MUST set
  // both min and max — leaving min alone makes the engine default
  // `minimumFractionDigits: 2` for USD, and if you've set max to 0
  // V8 throws "maximumFractionDigits value is out of range".
  const isWhole = Number.isInteger(Number(amount));
  const digits = isWhole ? 0 : 2;
  return Number(amount).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function _accessors(snapshot) {
  // Default to whatever we've fetched (may be null on cold start).
  const pkgs = snapshot?.packages || {};
  const fees = snapshot?.platform_fees || {};

  // Primary string-accessor — keeps existing call sites readable.
  const prices = (pkgId, fallback = "—") => {
    const amt = pkgs[pkgId]?.amount;
    return _fmtUsd(amt) ?? fallback;
  };
  prices.amount = (pkgId, fallback = 0) => {
    const amt = pkgs[pkgId]?.amount;
    return amt == null ? fallback : Number(amt);
  };
  prices.disabled = (pkgId) => !!pkgs[pkgId]?.disabled;
  prices.raw = (pkgId) => pkgs[pkgId] || null;

  // Platform-fee accessor — `fee("transport")` returns 0..1; `fee.pct(...)`
  // returns a percentage as a number (e.g. 10 for 10%).
  const fee = (name, fallback = 0) => {
    const v = fees[name]?.pct;
    return v == null ? fallback : Number(v);
  };
  fee.pct = (name, fallback = 0) => {
    const v = fees[name]?.pct;
    return v == null ? fallback : Number(v) * 100;
  };
  fee.raw = (name) => fees[name] || null;

  return { prices, fee, snapshot, ready: !!snapshot };
}

export function usePublicPrices() {
  const [snapshot, setSnapshot] = useState(_cache);

  useEffect(() => {
    let alive = true;
    const onUpdate = (s) => { if (alive) setSnapshot(s); };
    _subscribers.add(onUpdate);
    _fetchOnce().then((s) => { if (alive) setSnapshot(s); });
    return () => { alive = false; _subscribers.delete(onUpdate); };
  }, []);

  return _accessors(snapshot);
}

/** Force-refresh the cache (e.g. after the admin saves a price). */
export function invalidatePublicPrices() {
  _cache = null;
  return _fetchOnce();
}
